<script setup lang="ts">
import { ref, computed } from 'vue';
import { RouterLink } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useAuth } from '@/stores/auth';
import IconChevronDown from '@/components/icons/IconChevronDown.vue';
import IconProfile from '@/components/icons/IconProfile.vue';
import IconBell from '@/components/icons/IconBell.vue';
import IconWorkspace from '@/components/icons/IconWorkspace.vue';
import IconApprovals from '@/components/icons/IconApprovals.vue';
import IconMoon from '@/components/icons/IconMoon.vue';
import IconHelp from '@/components/icons/IconHelp.vue';
import IconSignOut from '@/components/icons/IconSignOut.vue';

const auth = useAuth();
const open = ref(false);
const rootRef = ref<HTMLElement | null>(null);
onClickOutside(rootRef, () => { open.value = false; });

const displayName = computed(() => auth.me?.displayName ?? '—');
const email = computed(() => auth.me?.email ?? '');
const roleLabel = computed(() => {
  const map: Record<string, string> = {
    'Board.President':  'President',
    'Board.Vice':       'Vice President',
    'Board.Treasurer':  'Treasurer',
    'Board.Secretary':  'Secretary',
    'Board.Member':     'Board member',
    'Board.BoardMember': 'Board member',
  };
  return map[auth.me?.role ?? ''] ?? (auth.me?.role ?? '');
});
const initials = computed(() => {
  const name = (auth.me?.displayName ?? '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
});

function toggleDark() {
  document.documentElement.classList.toggle('dark');
}

async function signOut() {
  open.value = false;
  await auth.logout();
}
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-navy-50 transition-colors"
      :class="{ 'bg-navy-50': open }"
      data-testid="user-pill"
      @click="open = !open"
    >
      <span class="w-8 h-8 rounded-pill bg-navy-700 text-white grid place-items-center text-caption font-semibold flex-shrink-0">
        {{ initials }}
      </span>
      <span class="flex-1 min-w-0 text-left">
        <span class="block text-body font-semibold text-fg truncate">{{ displayName }}</span>
        <span class="block text-caption text-muted truncate">{{ roleLabel }}</span>
      </span>
      <IconChevronDown
        class="w-3.5 h-3.5 text-muted flex-shrink-0 transition-transform"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <div
      v-if="open"
      class="absolute left-0 right-0 bottom-[calc(100%-4px)] bg-surface border border-border rounded-md shadow-lg p-1.5 z-[60]"
      data-testid="user-menu"
    >
      <div class="px-2.5 py-2.5 border-b border-border mb-1.5">
        <div class="text-body font-semibold text-fg">{{ displayName }}</div>
        <div class="text-[11.5px] text-muted font-mono mt-px">{{ email }}</div>
      </div>

      <RouterLink to="/settings" class="user-menu-item" @click="open = false">
        <IconProfile class="w-[15px] h-[15px] text-muted-2 flex-shrink-0" />
        My profile
      </RouterLink>
      <RouterLink to="/settings/notifications" class="user-menu-item" @click="open = false">
        <IconBell class="w-[15px] h-[15px] text-muted-2 flex-shrink-0" />
        Notification preferences
      </RouterLink>
      <RouterLink to="/settings" class="user-menu-item" @click="open = false">
        <IconWorkspace class="w-[15px] h-[15px] text-muted-2 flex-shrink-0" />
        Workspace settings
      </RouterLink>
      <RouterLink to="/residents/approvals" class="user-menu-item" @click="open = false">
        <IconApprovals class="w-[15px] h-[15px] text-muted-2 flex-shrink-0" />
        Resident approvals
      </RouterLink>
      <button class="user-menu-item" type="button" @click="toggleDark">
        <IconMoon class="w-[15px] h-[15px] text-muted-2 flex-shrink-0" />
        Switch to dark mode
      </button>

      <div class="h-px bg-border my-1.5 -mx-1.5" />

      <button class="user-menu-item" type="button" @click="open = false">
        <IconHelp class="w-[15px] h-[15px] text-muted-2 flex-shrink-0" />
        Help &amp; support
      </button>

      <div class="h-px bg-border my-1.5 -mx-1.5" />

      <button
        class="user-menu-item user-menu-item--danger"
        type="button"
        data-testid="sign-out"
        @click="signOut"
      >
        <IconSignOut class="w-[15px] h-[15px] flex-shrink-0" />
        Sign out
      </button>
    </div>
  </div>
</template>

<style scoped>
.user-menu-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 500;
  color: var(--color-fg-soft);
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
}
.user-menu-item:hover { background: var(--color-canvas-soft); color: var(--color-fg); }
.user-menu-item--danger { color: var(--color-red-700); }
.user-menu-item--danger:hover { background: var(--color-red-50); color: var(--color-red-700); }
.user-menu-item--danger :deep(svg) { color: var(--color-red-600); }
</style>
